import React, { useState, useRef } from "react";
import { Modal, message, Spin } from "antd";
import { ScanOutlined, LoadingOutlined, FilePdfOutlined } from "@ant-design/icons";
import { identifyIdCard } from "@ifa/utils";
import CustomButton from "./CustomButton";
import styles from "./OCRUpload.module.css";

interface OCRUploadProps {
  onSuccess: (data: any) => void;
  idType?: string;
  disabled?: boolean;
}

const OCRUpload: React.FC<OCRUploadProps> = ({ onSuccess, idType, disabled }) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (disabled || loading) return;
    inputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Handle preview
    if (file.type === "application/pdf") {
      setIsPdf(true);
      setPreviewUrl(null);
    } else {
      setIsPdf(false);
      // Create a local preview URL
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        setPreviewUrl(event.target?.result as string);
      };
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (idType) {
        formData.append("idType", idType);
      }

      const res = await identifyIdCard(formData);
      onSuccess(res);
      message.success("證件識別成功");
    } catch (error) {
      console.error("OCR Error", error);
    } finally {
      setLoading(false);
      setPreviewUrl(null); // Clear preview after close
      // Reset input to allow re-uploading the same file
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  return (
    <>
      {/* 隐藏的文件输入 */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={handleFileChange}
        style={{ display: "none" }}
        disabled={disabled}
      />

      {/* 可见的按钮 */}
      <CustomButton
        disabled={disabled}
        icon={<ScanOutlined />}
        bgColor="#00C7B6"
        color="#fff"
        width={130}
        height={32}
        fontSize={16}
        onClick={handleButtonClick}
      >
        證件掃描
      </CustomButton>

      <Modal open={loading} footer={null} closable={false} centered width={360} styles={{ body: { textAlign: "center", padding: "32px 24px" } }}>
        <div className={styles.scanContainer}>
          {isPdf ? (
            <FilePdfOutlined style={{ fontSize: 64, color: "#ff4d4f" }} />
          ) : previewUrl ? (
            <img src={previewUrl} className={styles.scanImage} alt="preview" />
          ) : (
            <Spin indicator={<LoadingOutlined className={styles.loadingIcon} spin />} />
          )}
          <div className={styles.scanLine} />
        </div>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12, color: "#333" }}>正在識別⋯⋯</div>
        <div
          style={{
            fontSize: 12,
            color: "#999",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <ScanOutlined />
          支援中國大陸/香港/澳門身份證/護照
        </div>
      </Modal>
    </>
  );
};

export default OCRUpload;
